import { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useServiceCategories } from "@/hooks/api-hooks";
import { getUrlParam, updateUrlParams } from "@/lib/format-utils";
import { setWebTitle } from "@/lib/format-utils";
import { localTenant } from "@/services/session-service";
import ServicesCategorySelector from "@/components/services-category-selector";
import ServicesCatalogBrowser from "@/components/services-catalog-browser";


export default function ServicesListPage(): JSX.Element {
    const { t } = useTranslation();
    const tenant = localTenant();
    const tenant_id = tenant?.id;

    const [searchParams, setSearchParams] = useSearchParams();

    const [dateRange, setDateRange] = useState<[string | null, string | null]>([
        searchParams.get('start_day') || null,
        searchParams.get('end_day') || null
    ]);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(searchParams.get('category') || null);

    setWebTitle(t('services'));

    const {
        isLoading: isLoadingCategories,
        data: categoriesData
    } = useServiceCategories(
        { tenant_id }
    );

    const handleCategoryClick = useCallback((tag: string) => {
        setSelectedCategory(tag);
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            newParams.set('category', tag);
            return newParams;
        });
    }, [setSearchParams]);

    const handleDateRangeChange = useCallback((range: [string | null, string | null]) => {
        setDateRange(range);
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            if (range[0] && range[1]) {
                newParams.set('start_day', range[0]);
                newParams.set('end_day', range[1]);
            } else if (!range[0] && !range[1]) {
                newParams.delete('start_day');
                newParams.delete('end_day');
            }
            return newParams;
        });
    }, [setSearchParams]);

    const categories = useMemo(() =>
        (categoriesData?.data || [])
            .filter(Boolean)
            .sort((a, b) => {
                const rankA = a.rank ?? Infinity;
                const rankB = b.rank ?? Infinity;
                return rankA - rankB;
            })
        , [categoriesData?.data, t]);

    // Update state from URL
    useEffect(() => {
        const categoryFromUrl = searchParams.get('category');
        if (categoryFromUrl && categoryFromUrl !== selectedCategory) {
            setSelectedCategory(categoryFromUrl);
        }
    }, [searchParams, selectedCategory]);

    // if no category is selected, set the first category
    useEffect(() => {
        if (!selectedCategory && categories.length > 0) {
            setSelectedCategory(categories[0].category);
        }
    }, [categories, selectedCategory]);

    const initialFilters = useMemo(() => {
        const filtersParam = getUrlParam('filters', '');
        if (filtersParam) {
            try {
                return JSON.parse(decodeURIComponent(filtersParam));
            } catch (e) {
                console.error('Failed to parse filters from URL:', e);
            }
        }
        return {};
    }, []);

    return (
        <div className="panel-content space-y-2">
            <ServicesCategorySelector
                categories={categories}
                selectedCategory={selectedCategory}
                isLoadingCategories={isLoadingCategories}
                onSelectCategory={handleCategoryClick}
            />
            {selectedCategory && (
                <ServicesCatalogBrowser
                    selectedCategory={selectedCategory}
                    dateRange={dateRange}
                    onDateRangeChange={handleDateRangeChange}
                    initialFilters={initialFilters}
                    onFiltersChange={(filters) => {
                        const hasActiveFilters = Object.values(filters).some(f => f && f.length > 0);
                        updateUrlParams({
                            filters: hasActiveFilters ? encodeURIComponent(JSON.stringify(filters)) : undefined
                        });
                    }}
                    enableBulkActions={true}
                />
            )}
        </div>
    );
}
